/**
 * Dummy data — 주소 검색 (카카오 API 미작동 시 fallback)
 * USE_DUMMY=true 시 이 모듈이 대신 사용됨
 */

import { getCurrentBeLang } from '../../contexts/LanguageContext';
import { translatePlaceName } from '../../i18n/placeNames';
import { translateBatch } from '../deepl';

const mockAddresses = [
  { place_name: '서울역', road_address_name: '서울특별시 용산구 한강대로 405', lat: 37.5547, lng: 126.9707 },
  { place_name: '강남역', road_address_name: '서울특별시 강남구 강남대로 396', lat: 37.4979, lng: 127.0276 },
  { place_name: '잠실역', road_address_name: '서울특별시 송파구 올림픽로 지하 265', lat: 37.5133, lng: 127.1001 },
  { place_name: '홍대입구역', road_address_name: '서울특별시 마포구 양화로 160', lat: 37.5571, lng: 126.9246 },
  { place_name: '여의도역', road_address_name: '서울특별시 영등포구 국회대로 지하 780', lat: 37.5217, lng: 126.9243 },
  { place_name: '서울시청', road_address_name: '서울특별시 중구 세종대로 110', lat: 37.5662, lng: 126.9779 },
  { place_name: '롯데월드타워', road_address_name: '서울특별시 송파구 올림픽로 300', lat: 37.5126, lng: 127.1026 },
  { place_name: '코엑스', road_address_name: '서울특별시 강남구 영동대로 513', lat: 37.5120, lng: 127.0590 },
  { place_name: '이태원', road_address_name: '서울특별시 용산구 이태원로 177', lat: 37.5346, lng: 126.9947 },
  { place_name: '명동역', road_address_name: '서울특별시 중구 퇴계로 지하 2', lat: 37.5609, lng: 126.9863 },
  { place_name: '신촌역', road_address_name: '서울특별시 서대문구 신촌역로 1', lat: 37.5550, lng: 126.9368 },
  { place_name: '건대입구역', road_address_name: '서울특별시 광진구 능동로 지하 110', lat: 37.5403, lng: 127.0699 },
  { place_name: '판교역', road_address_name: '경기도 성남시 분당구 판교역로 지하 160', lat: 37.3946, lng: 127.1114 },
  { place_name: '수원역', road_address_name: '경기도 수원시 팔달구 덕영대로 924', lat: 37.2660, lng: 127.0001 },
  { place_name: '인천공항', road_address_name: '인천광역시 중구 공항로 272', lat: 37.4602, lng: 126.4407 },
  { place_name: '김포공항', road_address_name: '서울특별시 강서구 하늘길 112', lat: 37.5585, lng: 126.7945 },
  { place_name: '서울대학교', road_address_name: '서울특별시 관악구 관악로 1', lat: 37.4602, lng: 126.9526 },
  { place_name: '연세대학교', road_address_name: '서울특별시 서대문구 연세로 50', lat: 37.5649, lng: 126.9389 },
  { place_name: '이화여자대학교', road_address_name: '서울특별시 서대문구 이화여대길 52', lat: 37.5628, lng: 126.9468 },
  { place_name: '삼성서울병원', road_address_name: '서울특별시 강남구 일원로 81', lat: 37.4881, lng: 127.0855 },
];

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isSubsequence(query, text) {
  let qi = 0;
  for (let ti = 0; ti < text.length && qi < query.length; ti++) {
    if (query[qi] === text[ti]) qi++;
  }
  return qi === query.length;
}

function relevanceScore(name, address, query) {
  const q = query.toLowerCase();
  const n = name.toLowerCase();
  const a = address.toLowerCase();
  if (n === q) return 0;
  if (n.startsWith(q)) return 1;
  if (n.includes(` ${q}`)) return 2;
  if (n.includes(q)) return 3;
  if (isSubsequence(q, n)) return 3.5;
  if (a.includes(q)) return 4;
  if (isSubsequence(q, a)) return 4.5;
  return 5;
}

/**
 * 더미 주소 검색 — 실제 API와 동일한 필터링/정렬 로직 적용
 */
export const searchAddress = async (query, options = {}) => {
  await delay(150);
  if (!query || !query.trim()) return [];
  const q = query.trim();

  const scored = mockAddresses.map((a) => ({
    name: a.place_name,
    address: a.road_address_name,
    _score: relevanceScore(a.place_name, a.road_address_name, q),
  }));

  const top = scored
    .filter((item) => item._score < 5)
    .sort((a, b) => {
      if (a._score !== b._score) return a._score - b._score;
      return a.name.length - b.name.length;
    })
    .slice(0, 10);

  const beLang = getCurrentBeLang();
  const nameStatic = top.map(({ name }) => translatePlaceName(name, beLang));
  const deeplNameIdx = [];
  nameStatic.forEach((translated, i) => {
    if (beLang !== 'ko' && translated === top[i].name) deeplNameIdx.push(i);
  });
  const deeplNameInputs = deeplNameIdx.map((i) => top[i].name);
  const addressInputs = top.map(({ address }) => address || '');

  const [deeplNameOutputs, addressOutputs] = await Promise.all([
    translateBatch(deeplNameInputs, beLang),
    translateBatch(addressInputs, beLang),
  ]);

  const nameI18nFinal = nameStatic.slice();
  deeplNameIdx.forEach((origIdx, i) => {
    nameI18nFinal[origIdx] = deeplNameOutputs[i] || nameStatic[origIdx];
  });

  return top.map(({ name, address }, i) => ({
    name,
    address,
    nameI18n: nameI18nFinal[i],
    addressI18n: addressOutputs[i] || address,
  }));
};
